/**
 * Notification preferences + dispatched notification records.
 *
 * Default matrix lives in code (DEFAULT_PREFERENCES) and is also seeded into
 * notification_preferences for new users — see ADR-0008.
 */
import { z } from 'zod';
import { IsoDateTimeSchema, UuidSchema } from './common.js';
import { NotificationChannelSchema, NotificationEventSchema, RoleSchema, } from './enums.js';
// ----------------------------------------------------------------------------
// Notification preference — per (user, event, channel)
// ----------------------------------------------------------------------------
export const NotificationPreferenceSchema = z.object({
    user_id: UuidSchema,
    event: NotificationEventSchema,
    channel: NotificationChannelSchema,
    enabled: z.boolean(),
    updated_at: IsoDateTimeSchema,
});
export const NotificationPreferenceUpdateSchema = z.object({
    event: NotificationEventSchema,
    channel: NotificationChannelSchema,
    enabled: z.boolean(),
});
// ----------------------------------------------------------------------------
// Sent / queued notification record
// ----------------------------------------------------------------------------
export const NotificationStatusSchema = z.enum([
    'queued',
    'sent',
    'delivered',
    'failed',
    'suppressed',
]);
export const NotificationSchema = z.object({
    id: UuidSchema,
    recipient_user_id: UuidSchema,
    event: NotificationEventSchema,
    channel: NotificationChannelSchema,
    project_id: UuidSchema.nullable(),
    task_id: UuidSchema.nullable(),
    status: NotificationStatusSchema,
    payload: z.record(z.unknown()),
    error: z.string().nullable(),
    queued_at: IsoDateTimeSchema,
    sent_at: IsoDateTimeSchema.nullable(),
    delivered_at: IsoDateTimeSchema.nullable(),
});
// ----------------------------------------------------------------------------
// Notification log — supervisor/admin audit of dispatched notifications.
// One row per (event, channel, recipient): what was sent, to whom, when, and
// whether it landed. Joins the recipient + project so the log reads on its own.
// ----------------------------------------------------------------------------
/** Did it actually go out? Refines a 'sent' row for the audit log. */
export const NotificationDeliverySchema = z.enum([
    'real', // delivered via the provider (has a receipt id)
    'stub', // logged only in stub mode — not actually sent
    'diverted', // really sent, but rerouted (divert-to-admins)
    'suppressed', // held by the delivery mode (off / non-email while diverting)
    'failed',
    'queued',
    'unknown', // older rows recorded before delivery was instrumented
]);
export const NotificationLogEntrySchema = z.object({
    id: UuidSchema,
    event: NotificationEventSchema,
    channel: NotificationChannelSchema,
    status: NotificationStatusSchema,
    /** Whether the send truly went out (real / stub / diverted / …). */
    delivery: NotificationDeliverySchema,
    /** Where a diverted send actually landed (else null). */
    delivered_to: z.string().nullable(),
    recipient_user_id: UuidSchema.nullable(),
    recipient_name: z.string().nullable(),
    recipient_email: z.string().nullable(),
    recipient_role: RoleSchema.nullable(),
    project_id: UuidSchema.nullable(),
    project_title: z.string().nullable(),
    subject: z.string().nullable(),
    error: z.string().nullable(),
    queued_at: IsoDateTimeSchema,
    sent_at: IsoDateTimeSchema.nullable(),
});
/** Counts by status for the current channel/event/recipient filter. */
export const NotificationLogSummarySchema = z.object({
    sent: z.number().int().min(0),
    delivered: z.number().int().min(0),
    failed: z.number().int().min(0),
    queued: z.number().int().min(0),
    suppressed: z.number().int().min(0),
});
export const NotificationLogResponseSchema = z.object({
    data: z.array(NotificationLogEntrySchema),
    total: z.number().int().min(0),
    summary: NotificationLogSummarySchema,
});
// ----------------------------------------------------------------------------
// Notification delivery settings — admin-configurable external routing.
//   normal           → send to the real resolved recipients
//   divert_to_admins → email rerouted to admins; Teams/SMS/push held
//   disabled         → no external sends (the in-app feed still works)
// ----------------------------------------------------------------------------
export const NotificationDeliveryModeSchema = z.enum([
    'normal',
    'divert_to_admins',
    'disabled',
]);
/** An admin who can be picked as a divert recipient. */
export const AdminOptionSchema = z.object({
    id: UuidSchema,
    name: z.string(),
    email: z.string(),
});
export const NotificationSettingsSchema = z.object({
    mode: NotificationDeliveryModeSchema,
    updated_at: IsoDateTimeSchema.nullable(),
    /** Whether the underlying email capability is armed (key set + infra stub off). */
    email_armed: z.boolean(),
    /** All active admins — the options for the divert-recipient picker. */
    admins: z.array(AdminOptionSchema),
    /** Selected divert recipients (user ids); empty = all admins. */
    divert_recipient_ids: z.array(UuidSchema),
    /** The email addresses a divert would actually hit right now (for display). */
    divert_emails: z.array(z.string()),
});
export const NotificationSettingsUpdateSchema = z
    .object({
    mode: NotificationDeliveryModeSchema.optional(),
    divert_recipient_ids: z.array(UuidSchema).optional(),
})
    .refine((d) => d.mode !== undefined || d.divert_recipient_ids !== undefined, {
    message: 'Provide mode and/or divert_recipient_ids',
});
// ----------------------------------------------------------------------------
// Reminder settings (Phase 2) — admin-editable cadence for the reminder sweep
// (runs hourly). Singleton, mirrors NotificationSettings.
// ----------------------------------------------------------------------------
/** Unit for a lead/interval value — admins switch between days and hours. */
export const ReminderUnitSchema = z.enum(['days', 'hours']);
export const ReminderSettingsSchema = z.object({
    upcoming_job_enabled: z.boolean(),
    /** Remind assigned crew + PM this far before a scheduled job (in *_unit). */
    upcoming_job_lead_days: z.number().int().min(0).max(90),
    upcoming_job_lead_unit: ReminderUnitSchema,
    pickup_reminder_enabled: z.boolean(),
    pickup_reminder_lead_days: z.number().int().min(0).max(90),
    pickup_reminder_lead_unit: ReminderUnitSchema,
    registration_reminder_enabled: z.boolean(),
    /** Day thresholds before a vehicle registration/inspection lapses (days only). */
    registration_lead_days: z.array(z.number().int().min(1).max(120)).max(6),
    daily_summary_enabled: z.boolean(),
    // Task-ending-soon (demo #8): nudge assigned crew this many minutes before a
    // task's committed/scheduled end so they complete or extend. Minute-granular,
    // so it carries its own minutes value rather than a days/hours unit.
    task_ending_reminder_enabled: z.boolean(),
    task_ending_reminder_lead_minutes: z.number().int().min(1).max(120),
    // Approval reminders (Phase 2b): how often (interval, in *_unit) + max times.
    request_approval_reminder_enabled: z.boolean(),
    request_approval_reminder_interval_days: z.number().int().min(1).max(90),
    request_approval_reminder_interval_unit: ReminderUnitSchema,
    request_approval_reminder_max: z.number().int().min(0).max(10),
    bid_approval_reminder_enabled: z.boolean(),
    bid_approval_reminder_interval_days: z.number().int().min(1).max(90),
    bid_approval_reminder_interval_unit: ReminderUnitSchema,
    bid_approval_reminder_max: z.number().int().min(0).max(10),
    updated_at: IsoDateTimeSchema.nullable(),
});
export const ReminderSettingsUpdateSchema = ReminderSettingsSchema.omit({
    updated_at: true,
}).partial();
// ----------------------------------------------------------------------------
// Default notification matrix (from ADR-0008)
// Format: role → event → channels[]
// ----------------------------------------------------------------------------
export const DEFAULT_NOTIFICATION_MATRIX = {
    // Defaults reflect the ACTIVE channels (email / Teams / in-app — see
    // recipients.ts ACTIVE_CHANNELS); SMS + web push are held off, so they're
    // omitted here rather than defaulted to dead rows. In-app is on every event a
    // role receives (the written record in the bell), email on the milestones,
    // Teams where the role collaborates. Each role's events match recipients.ts.
    // note_added defaults to in-app only — notes are frequent, so the bell is the
    // low-noise default; any role can opt into email/Teams in Settings.
    crew: {
        // Crew are emailed too — they're often in the field without Teams open.
        project_scheduled: ['email', 'teams', 'in_app'],
        // A scheduled task they're on was cancelled/removed — don't show up for it.
        task_cancelled: ['email', 'teams', 'in_app'],
        // A blocked project they're on cleared.
        blocked_resolved: ['email', 'teams', 'in_app'],
        // A job they're on moved — same reach as the initial schedule.
        project_rescheduled: ['email', 'teams', 'in_app'],
        crew_assignment_changed: ['email', 'teams', 'in_app'],
        blocked_needs_attention: ['email', 'teams', 'in_app'],
        vehicle_issue_resolved: ['email', 'teams', 'in_app'],
        // Crew are the usual punch-item assignees.
        punch_item_assigned: ['email', 'teams', 'in_app'],
        // Crew want the latest plans/paperwork.
        document_added: ['email', 'teams', 'in_app'],
        // Day-before reminders for the jobs they're on.
        upcoming_job: ['email', 'teams', 'in_app'],
        pickup_reminder: ['email', 'teams', 'in_app'],
        // A task they're on is ~5 min from its scheduled end (demo #8). A transient
        // nudge to complete/extend — Teams + bell only (email would be inbox noise);
        // crew can opt into email in Settings.
        task_ending_soon: ['teams', 'in_app'],
        // Project start/end nudges (E) — same transient Teams + bell as a task nudge.
        project_starting_soon: ['teams', 'in_app'],
        project_ending_soon: ['teams', 'in_app'],
        // Pickup start nudge (C) — same transient Teams + bell.
        pickup_starting_soon: ['teams', 'in_app'],
        // Notes stay in-app for crew — emailed notes go to PM + supervisors only.
        note_added: ['in_app'],
    },
    supervisor: {
        project_approval_requested: ['email', 'teams', 'in_app'],
        project_approved: ['teams', 'in_app'],
        approval_changes_requested: ['email', 'teams', 'in_app'],
        project_changes_requested: ['in_app'],
        project_rescheduled: ['teams', 'in_app'],
        task_requested: ['email', 'teams', 'in_app'],
        task_confirmed: ['in_app'],
        crew_assignment_changed: ['teams', 'in_app'],
        en_route: ['email', 'teams', 'in_app'],
        on_site: ['email', 'teams', 'in_app'],
        task_started: ['email', 'teams', 'in_app'],
        task_completed: ['email', 'teams', 'in_app'],
        project_finished: ['email', 'teams', 'in_app'],
        // Project start/end nudges (E) — Leo (supervisor) works installs too and
        // asked for the email; crew get Teams/in-app via their own row.
        project_starting_soon: ['email', 'teams', 'in_app'],
        project_ending_soon: ['email', 'teams', 'in_app'],
        pickup_starting_soon: ['email', 'teams', 'in_app'],
        punch_walkthrough_ready: ['teams', 'in_app'],
        complete_recap_available: ['email', 'teams', 'in_app'],
        blocked_needs_attention: ['email', 'teams', 'in_app'],
        blocked_resolved: ['email', 'teams', 'in_app'],
        vehicle_issue_reported: ['email', 'teams', 'in_app'],
        vehicle_out_of_service: ['teams', 'in_app'],
        vehicle_issue_resolved: ['email', 'teams', 'in_app'],
        punch_item_added: ['email', 'teams', 'in_app'],
        punch_item_updated: ['email', 'teams', 'in_app'],
        punch_item_resolved: ['email', 'teams', 'in_app'],
        punch_item_assigned: ['email', 'teams', 'in_app'],
        document_added: ['email', 'teams', 'in_app'],
        registration_expiring: ['email', 'teams', 'in_app'],
        daily_dispatch_summary: ['email', 'teams'],
        request_approval_reminder: ['email', 'teams', 'in_app'],
        note_added: ['email', 'teams', 'in_app'],
    },
    pm: {
        project_scheduled: ['email', 'teams', 'in_app'],
        project_approved: ['email', 'teams', 'in_app'],
        approval_changes_requested: ['in_app'],
        project_changes_requested: ['email', 'teams', 'in_app'],
        project_rescheduled: ['email', 'teams', 'in_app'],
        task_confirmed: ['email', 'teams', 'in_app'],
        en_route: ['email', 'teams', 'in_app'],
        on_site: ['email', 'teams', 'in_app'],
        task_started: ['email', 'teams', 'in_app'],
        task_completed: ['email', 'teams', 'in_app'],
        project_finished: ['email', 'teams', 'in_app'],
        punch_walkthrough_ready: ['email', 'teams', 'in_app'],
        complete_recap_available: ['email', 'teams', 'in_app'],
        blocked_needs_attention: ['email', 'teams', 'in_app'],
        blocked_resolved: ['email', 'teams', 'in_app'],
        task_cancelled: ['email', 'teams', 'in_app'],
        punch_item_added: ['email', 'teams', 'in_app'],
        punch_item_updated: ['email', 'teams', 'in_app'],
        punch_item_resolved: ['email', 'teams', 'in_app'],
        document_added: ['email', 'teams', 'in_app'],
        upcoming_job: ['email', 'in_app'],
        pickup_reminder: ['email', 'teams', 'in_app'],
        bid_approval_reminder: ['email', 'teams', 'in_app'],
        note_added: ['email', 'teams', 'in_app'],
    },
    sales: {
        complete_recap_available: ['email', 'in_app'],
        note_added: ['in_app'],
    },
    // Admins are resolved alongside supervisors (recipients.ts), so mirror them.
    admin: {
        // New account/access requests land with admins, who approve them.
        access_requested: ['email', 'teams', 'in_app'],
        project_approval_requested: ['email', 'teams', 'in_app'],
        project_approved: ['teams', 'in_app'],
        approval_changes_requested: ['email', 'teams', 'in_app'],
        project_changes_requested: ['in_app'],
        project_rescheduled: ['teams', 'in_app'],
        task_requested: ['email', 'teams', 'in_app'],
        task_confirmed: ['in_app'],
        crew_assignment_changed: ['teams', 'in_app'],
        en_route: ['email', 'teams', 'in_app'],
        on_site: ['email', 'teams', 'in_app'],
        task_started: ['email', 'teams', 'in_app'],
        task_completed: ['email', 'teams', 'in_app'],
        project_finished: ['email', 'teams', 'in_app'],
        // Project start/end nudges (E) — Leo (supervisor) works installs too and
        // asked for the email; crew get Teams/in-app via their own row.
        project_starting_soon: ['email', 'teams', 'in_app'],
        project_ending_soon: ['email', 'teams', 'in_app'],
        pickup_starting_soon: ['email', 'teams', 'in_app'],
        punch_walkthrough_ready: ['teams', 'in_app'],
        complete_recap_available: ['email', 'teams', 'in_app'],
        blocked_needs_attention: ['email', 'teams', 'in_app'],
        blocked_resolved: ['email', 'teams', 'in_app'],
        vehicle_issue_reported: ['email', 'teams', 'in_app'],
        vehicle_out_of_service: ['teams', 'in_app'],
        vehicle_issue_resolved: ['email', 'teams', 'in_app'],
        punch_item_added: ['email', 'teams', 'in_app'],
        punch_item_updated: ['email', 'teams', 'in_app'],
        punch_item_resolved: ['email', 'teams', 'in_app'],
        punch_item_assigned: ['email', 'teams', 'in_app'],
        document_added: ['email', 'teams', 'in_app'],
        registration_expiring: ['email', 'teams', 'in_app'],
        daily_dispatch_summary: ['email', 'teams'],
        request_approval_reminder: ['email', 'teams', 'in_app'],
        note_added: ['email', 'teams', 'in_app'],
    },
};
/** Flatten the default matrix into preference rows for seed/onboarding. */
export function defaultPreferenceRows(role, userId) {
    const rows = [];
    const events = NotificationEventSchema.options;
    const channels = NotificationChannelSchema.options;
    const now = new Date().toISOString();
    for (const event of events) {
        const enabledChannels = DEFAULT_NOTIFICATION_MATRIX[role]?.[event] ?? [];
        for (const channel of channels) {
            rows.push({
                user_id: userId,
                event,
                channel,
                enabled: enabledChannels.includes(channel),
                updated_at: now,
            });
        }
    }
    return rows;
}
//# sourceMappingURL=notifications.js.map